import React from 'react';
import { JsonApiDisplay } from '../components/chat/JsonApiDisplay';

interface ContentSegment {
  type: 'text' | 'json';
  content: string;
  jsonData?: any;
  language?: string;
}

/**
 * Parses message content to identify and extract JSON code blocks
 * Returns an array of segments that are either text or JSON
 */
export const parseContentWithJson = (content: string): ContentSegment[] => {
  const segments: ContentSegment[] = [];
  
  // Match code blocks with optional language identifier
  // Matches: ```json\n...``` or ```\n...``` where content looks like JSON
  const codeBlockRegex = /```(\w+)?\n([\s\S]*?)```/g;
  
  let lastIndex = 0;
  let match;
  
  while ((match = codeBlockRegex.exec(content)) !== null) {
    const [fullMatch, language, code] = match;
    const matchStart = match.index;
    
    // Add any text before this code block
    if (matchStart > lastIndex) {
      const textBefore = content.substring(lastIndex, matchStart).trim();
      if (textBefore) {
        segments.push({
          type: 'text',
          content: textBefore,
        });
      }
    }
    
    // Try to parse as JSON
    let isJson = false;
    let jsonData = null;
    
    // If language is explicitly 'json' or code starts with { or [, try parsing
    if (language === 'json' || code.trim().startsWith('{') || code.trim().startsWith('[')) {
      try {
        jsonData = JSON.parse(code.trim());
        isJson = true;
      } catch (e) {
        // Not valid JSON, treat as text
        isJson = false;
      }
    }
    
    if (isJson && jsonData) {
      segments.push({
        type: 'json',
        content: code.trim(),
        jsonData,
        language: language || 'json',
      });
    } else {
      // Not JSON or parsing failed, include as text with code block
      segments.push({
        type: 'text',
        content: fullMatch,
      });
    }
    
    lastIndex = matchStart + fullMatch.length;
  }
  
  // Add any remaining text after the last code block
  if (lastIndex < content.length) {
    const remainingText = content.substring(lastIndex).trim();
    if (remainingText) {
      segments.push({
        type: 'text',
        content: remainingText,
      });
    }
  }
  
  // If no segments were added, return the entire content as text
  if (segments.length === 0) {
    segments.push({
      type: 'text',
      content: content,
    });
  }
  
  return segments;
};

/**
 * Renders parsed content segments with special handling for JSON
 */
export const renderParsedContent = (segments: ContentSegment[]): React.ReactNode => {
  return segments.map((segment, index) => {
    if (segment.type === 'json' && segment.jsonData) {
      return (
        <div key={index}>
          <JsonApiDisplay 
            data={segment.jsonData}
            title="View JSON Response"
            allowExpand={true}
          />
        </div>
      );
    } else {
      return (
        <div key={index} className="whitespace-pre-wrap">
          {segment.content}
        </div>
      );
    }
  });
};

